<template>
  <a-button
    v-bind="$attrs"
    :loading="props.loading"
    :disabled="props.disabled || props.loading"
    :class="[
      'gtc-button',
      `gtc-button-${props.size}`,
      `gtc-button-${props.type}`,
      `gtc-button-mode-${props.mode}`,
      {
        'gtc-button-disabled': props.disabled,
        'gtc-button-loading': props.loading,
        'gtc-button-block': props.block,
        'gtc-button-icon-button': props.iconButton,
        'gtc-button-icon-button-sm': props.iconButton && props.size === 'sm',
        'gtc-button-icon-button-md': props.iconButton && props.size === 'md',
        'gtc-button-icon-button-lg': props.iconButton && props.size === 'lg',
        'gtc-button-icon-button-xl': props.iconButton && props.size === 'xl',
      },
    ]"
  >
    <template v-if="!props.iconButton">
      <SvgIcon
        v-if="props.prefixIcon && !props.loading"
        :name="props.prefixIcon"
        :size="props.size === 'sm' ? 12 : props.size === 'md' ? 14 : 16"
        :style="{ fill: currentIconColor }"
      />
      <slot></slot>
      <SvgIcon
        v-if="props.suffixIcon"
        :name="props.suffixIcon"
        :size="props.size === 'sm' ? 12 : props.size === 'md' ? 14 : 16"
        :style="{ fill: currentIconColor }"
      />
    </template>
    <SvgIcon
      v-if="props.icon && props.iconButton"
      :name="props.icon"
      :size="props.size === 'sm' ? 12 : props.size === 'md' ? 14 : 16"
      :style="{ fill: currentIconColor }"
    />
  </a-button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
const props = defineProps({
  size: {
    type: String,
    default: 'md',
  },
  type: {
    type: String,
    default: 'default',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  block: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'default',
  },
  prefixIcon: {
    type: String,
    default: '',
  },
  suffixIcon: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  iconButton: {
    type: Boolean,
    default: false,
  },
})

const currentIconColor = computed(() => {
  if (props.mode === 'default') {
    return 'var(--text-fixed-l0)'
  }
  if (props.type === 'primary' && props.mode === 'light') {
    return 'var(--blue-primary)'
  }
  if (props.type === 'primary' && props.mode === 'link') {
    return 'var(--blue-500)'
  }
  if (props.type === 'danger' && props.mode === 'light') {
    return 'var(--danger-500)'
  }
  if (props.type === 'danger' && props.mode === 'link') {
    return 'var(--danger-500)'
  }
  if (props.type === 'success' && props.mode === 'light') {
    return 'var(--success-500)'
  }
  if (props.type === 'success' && props.mode === 'link') {
    return 'var(--success-500)'
  }
  return 'var(--text-fixed-l0)'
})
</script>

<style lang="less" scoped>
.gtc-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  box-shadow: none;
}

// 主题色
.gtc-button-primary.gtc-button-mode-default {
  background-color: var(--sec-500);
  color: var(--text-fixed-l0);
  &:hover {
    background-color: var(--sec-400);
  }
  &:active {
    background-color: var(--sec-500);
  }
  &:disabled {
    opacity: 0.4;
  }
}

.gtc-button-primary.gtc-button-mode-light {
  background-color: var(--blue-primary-bg);
  color: var(--blue-primary);
  &:hover {
    background-color: var(--ground-100);
  }
  &:active {
    background-color: var(--ground-200);
  }
  &:disabled {
    opacity: 0.4;
  }
}

.gtc-button-primary.gtc-button-mode-link {
  color: var(--blue-500);
  &:hover {
    background-color: var(--ground-50);
  }
  &:active {
    background-color: var(--ground-100);
  }
  &:disabled {
    opacity: 0.4;
  }
}

// 警告色
.gtc-button-danger.gtc-button-mode-default {
  background-color: var(--danger-500);
  color: var(--text-fixed-l0);
  &:hover {
    background-color: var(--danger-400);
  }
  &:active {
    background-color: var(--danger-500);
  }
  &:disabled {
    opacity: 0.4;
  }
}

.gtc-button-danger.gtc-button-mode-light {
  background-color: var(--danger-bg);
  color: var(--danger-500);
  &:hover {
    background-color: var(--ground-100);
  }
  &:active {
    background-color: var(--ground-200);
  }
  &:disabled {
    background-color: var(--ground-100);
    opacity: 0.4;
  }
}

.gtc-button-danger.gtc-button-mode-link {
  color: var(--danger-500);
  &:hover {
    background-color: var(--danger-50);
  }
  &:active {
    background-color: var(--ground-100);
  }
  &:disabled {
    opacity: 0.4;
  }
}

// 成功色
.gtc-button-success.gtc-button-mode-default {
  background-color: var(--success-500);
  color: var(--text-fixed-l0);
  &:hover {
    background-color: var(--success-400);
  }
  &:active {
    background-color: var(--success-500);
  }
  &:disabled {
    opacity: 0.4;
  }
}

.gtc-button-success.gtc-button-mode-light {
  background-color: var(--success-bg);
  color: var(--success-500);
  &:hover {
    background-color: var(--success-100);
  }
  &:active {
    background-color: var(--success-200);
  }
  &:disabled {
    background-color: var(--success-100);
    opacity: 0.4;
  }
}

.gtc-button-success.gtc-button-mode-link {
  color: var(--success-500);
  &:hover {
    background-color: var(--success-50);
  }
  &:active {
    background-color: var(--success-100);
  }
  &:disabled {
    opacity: 0.4;
  }
}

// 尺寸
.gtc-button-sm {
  padding: 2px 12px;
  height: 24px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.gtc-button-md {
  padding: 4px 14px;
  height: 30px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
}

.gtc-button-lg {
  padding: 8px 16px;
  height: 38px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
}

.gtc-button-xl {
  padding: 9px 20px;
  height: 42px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 10px;
}

.gtc-button-icon-button {
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gtc-button-icon-button-sm {
  width: 24px;
  height: 24px;
}

.gtc-button-icon-button-md {
  width: 30px;
  height: 30px;
}

.gtc-button-icon-button-lg {
  width: 38px;
  height: 38px;
}

.gtc-button-icon-button-xl {
  width: 42px;
  height: 42px;
}
</style>
